// src/components/Sidebar/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { tools } from '../../data/toolsData';
import { Link, useLocation } from 'react-router-dom';
import { List, Receipt, Home, User, CreditCard, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LoginButton from '../Auth/LoginButton';
import { createApi } from '../../services/api';
import MpesaPayment from '../MpesaPayment/MpesaPayment';

const Sidebar = ({ selectedTool, onSelectTool, creditBalance }) => {
  const { user, token } = useAuth();
  const [balance, setBalance] = useState(creditBalance || null);
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      if (token) {
        try {
          const api = createApi(token);
          const response = await api.getBalance();
          setBalance(response.balance);
        } catch (error) {
          console.error('Error fetching balance:', error);
          setBalance(0);
        }
      }
    };
    
    fetchBalance();
  }, [token]);

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const handleOpenPaymentModal = () => {
    console.log("Opening payment modal from sidebar");
    setShowPaymentForm(true);
  };

  const handlePaymentComplete = async () => {
    console.log("Payment completed, closing modal");
    setShowPaymentForm(false);
    // Refresh balance after payment
    if (token) {
      try {
        const api = createApi(token);
        const response = await api.getBalance();
        setBalance(response.balance);
      } catch (error) {
        console.error('Error fetching balance after payment:', error);
      }
    }
  };

  return (
    <>
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 w-full bg-white z-30 px-4 py-3 flex justify-between items-center shadow-sm">
        <div className="flex items-center">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-3 rounded-lg text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
          <h1 className="ml-3 text-xl font-bold text-primary-800">eWakili Kenya</h1>
        </div>
        {user && (
          <div className="flex items-center space-x-2">
            <span className="text-sm font-semibold text-primary-600">
              {balance !== null ? `${balance} Credits` : ''}
            </span>
            {user.photoURL ? (
              <img 
                src={user.photoURL} 
                alt="Profile" 
                className="h-8 w-8 rounded-full border-2 border-primary-100"
              />
            ) : (
              <User className="h-8 w-8 p-1 bg-primary-100 rounded-full text-primary-600" />
            )}
          </div>
        )}
      </div>

      {/* Mobile Sidebar Overlay */}
      <div 
        className={`md:hidden fixed inset-0 bg-gray-900 bg-opacity-50 z-20 transition-opacity duration-200 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Sidebar */}
      <div 
        className={`fixed top-0 left-0 h-screen bg-white z-30 w-72 sm:w-80 md:w-56 lg:w-64 transition-transform duration-300 ease-in-out transform shadow-xl md:shadow-none md:translate-x-0 ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } md:static md:h-screen md:bg-primary-900 md:flex md:flex-col`}
      >
        <div className="h-full overflow-y-auto">
          {/* Logo/Title Section */}
          <div className="bg-primary-50 p-4 mb-6 md:mb-8 md:bg-transparent">
            <h1 className="text-xl font-bold text-primary-900 md:text-white">
              Kenya eWakili Legal AI
            </h1>
            <p className="text-xs text-primary-700 mt-1 opacity-80 md:text-primary-100">Your Kenyan Legal Assistant</p>
          </div>

          {/* User Profile Section */}
          {user && (
            <div className="mb-6 px-4">
              <div className="bg-primary-800 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  {user.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt="Profile" 
                      className="h-10 w-10 rounded-full border-2 border-primary-300/30"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <User className="h-6 w-6 text-primary-700" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user.displayName || 'User'}
                    </p>
                    <p className="text-xs text-primary-200 truncate">
                      {user.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Credits Section */}
          {user && (
            <div className="mb-6 px-4">
              <div className="bg-primary-800/80 rounded-lg p-4">
                <h2 className="text-sm font-medium text-primary-100 mb-2 flex items-center">
                  <CreditCard className="h-4 w-4 mr-2 opacity-70" />
                  Available Credits
                </h2>
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-white">{balance ?? '...'}</span>
                  <button 
                    onClick={handleOpenPaymentModal}
                    className="ml-auto text-xs bg-primary-600 hover:bg-primary-500 text-white rounded-full px-3 py-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300"
                    aria-label="Add more credits"
                  >
                    Top up
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <div className="mb-6 px-4">
            <h2 className="text-xs uppercase tracking-wider text-primary-300/70 mb-2 px-2">Navigation</h2>
            <div className="space-y-1">
              <Link 
                to="/" 
                className={`flex items-center w-full rounded-lg px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                  isActivePath('/') 
                    ? 'bg-primary-700 text-white' 
                    : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                }`}
              >
                <Home className="h-5 w-5 mr-3" />
                <span>Home</span>
              </Link>
              
              {user && (
                <>
                  <Link 
                    to="/history" 
                    className={`flex items-center w-full rounded-lg px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                      isActivePath('/history') 
                        ? 'bg-primary-700 text-white' 
                        : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                    }`}
                  >
                    <List className="h-5 w-5 mr-3" />
                    <span>Research History</span>
                  </Link>
                  
                  <Link 
                    to="/transactions" 
                    className={`flex items-center w-full rounded-lg px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                      isActivePath('/transactions') 
                        ? 'bg-primary-700 text-white' 
                        : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                    }`}
                  >
                    <Receipt className="h-5 w-5 mr-3" />
                    <span>Transaction History</span>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Services Section */}
          {user && (
            <div className="mb-6 px-4">
              <h2 className="text-xs uppercase tracking-wider text-primary-300/70 mb-2 px-2">Services</h2>
              <div className="space-y-1">
                {tools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => tool.available && onSelectTool?.(tool.id)}
                    className={`flex items-center justify-between w-full rounded-lg px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300
                      ${selectedTool === tool.id
                        ? 'bg-primary-700 text-white'
                        : 'text-primary-100 hover:bg-primary-800 hover:text-white'}
                      ${!tool.available && 'opacity-50 cursor-not-allowed'}`}
                    disabled={!tool.available}
                    aria-disabled={!tool.available}
                  >
                    <div className="flex items-center">
                      <tool.icon className="h-5 w-5 mr-3" />
                      <span>{tool.name}</span>
                    </div>
                    {tool.available && (
                      <ChevronRight className="h-4 w-4 opacity-70" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Auth Section - at bottom */}
          <div className="mt-auto px-4 pb-6 pt-2">
            <div className="bg-primary-800 rounded-lg overflow-hidden">
              <LoginButton />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]"
          onClick={() => setShowPaymentForm(false)} // Close when clicking outside
        >
          <div 
            className="bg-white rounded-xl w-full max-w-md animate-fade-in-up shadow-2xl"
            onClick={(e) => e.stopPropagation()} // Prevent clicks inside from closing the modal
          >
            <MpesaPayment
              minAmount={100}
              onClose={() => setShowPaymentForm(false)}
              onPaymentComplete={handlePaymentComplete}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;